import React, { useRef, useEffect, useState } from 'react';

const ITEM_H = 56;
const VISIBLE = 5;
const PAD = ITEM_H * 2; // padding to center first/last items

export default function ScrollPicker({ items, value, onChange }) {
  const ref = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const ticking = useRef(false);

  const selectedIdx = Math.max(0, items.findIndex(i => i.value === value));

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = selectedIdx * ITEM_H;
      setScrollY(selectedIdx * ITEM_H);
    }
  }, []); // eslint-disable-line

  const onScroll = () => {
    if (!ticking.current) {
      requestAnimationFrame(() => {
        if (!ref.current) return;
        const top = ref.current.scrollTop;
        setScrollY(top);
        const idx = Math.round(top / ITEM_H);
        if (items[idx] && items[idx].value !== value) {
          onChange(items[idx].value);
        }
        ticking.current = false;
      });
      ticking.current = true;
    }
  };

  const currentIdx = Math.round(scrollY / ITEM_H);

  return (
    <div style={styles.outer}>
      {/* highlight bar on the selected row */}
      <div style={styles.highlight} />

      <div
        ref={ref}
        onScroll={onScroll}
        style={styles.scroller}
      >
        {/* top padding */}
        <div style={{ height: PAD, flexShrink: 0 }} />

        {items.map((item, i) => {
          const dist = Math.abs(i - currentIdx);
          const opacity = dist === 0 ? 1 : dist === 1 ? 0.45 : 0.2;
          const color   = dist === 0 ? '#3d5ef5' : '#555';
          const fontSize = dist === 0 ? 19 : 16;
          const fontWeight = dist === 0 ? '700' : '400';
          return (
            <div
              key={item.value}
              style={{
                ...styles.item,
                opacity,
                color,
                fontSize,
                fontWeight,
              }}
              onClick={() => {
                onChange(item.value);
                ref.current.scrollTop = i * ITEM_H;
              }}
            >
              {item.label}
            </div>
          );
        })}

        {/* bottom padding */}
        <div style={{ height: PAD, flexShrink: 0 }} />
      </div>
    </div>
  );
}

const styles = {
  outer: {
    position: 'relative',
    height: ITEM_H * VISIBLE,
    overflow: 'hidden',
    flex: 1,
  },
  highlight: {
    position: 'absolute',
    top: ITEM_H * 2,
    left: 0,
    right: 0,
    height: ITEM_H,
    background: '#eef0ff',
    borderRadius: 10,
    zIndex: 0,
    pointerEvents: 'none',
  },
  scroller: {
    height: '100%',
    overflowY: 'scroll',
    scrollSnapType: 'y mandatory',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    zIndex: 1,
  },
  item: {
    height: ITEM_H,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    scrollSnapAlign: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'font-size 0.1s, opacity 0.1s',
  },
};
